(() => {
  class AspadzProductHowToVideo extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.progressFrame = null;
      this.videoProgressFrame = null;
      this.activeMedia = null;
      this.activeFrame = null;
      this.activeVideo = null;
      this.activeIframe = null;
      this.activeVideoTrigger = null;
      this.autoplayDelay = 5000;
      this.progressScale = 0;
      this.isAutoplayLocked = false;
      this.isStoppingVideo = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onClick = this.onClick.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleSlideChangeTransitionStart = this.handleSlideChangeTransitionStart.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      this.handleVideoProgress = this.handleVideoProgress.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addMotionListener();
      this.init();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.clearTimeout(this.retryTimer);
      this.removeMotionListener();
      this.stopAutoplayProgress();
      this.stopInlineVideo({ resetProgress: true });

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    addMotionListener() {
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.addListener(this.handleMotionPreferenceChange);
      }
    }

    removeMotionListener() {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionPreferenceChange);
      }
    }

    init() {
      if (this.swiper) return;

      const slider = this.querySelector('[data-aspadz-product-how-to-video-slider]');
      const slides = this.querySelectorAll('.aspadz-product-how-to-video__slide');
      if (!slider || slides.length < 2) {
        this.handleMotionPreferenceChange();
        return;
      }

      if (typeof window.Swiper === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      const delay = Number(this.dataset.autoplayDelay) || 5000;
      const pagination = this.querySelector('[data-aspadz-product-how-to-video-pagination]');
      this.autoplayDelay = delay;
      this.style.setProperty('--aspadz-phtv-delay', `${delay}ms`);
      this.setProgressScale(0);

      this.swiper = new window.Swiper(slider, {
        allowTouchMove: true,
        autoplay: false,
        loop: true,
        loopAdditionalSlides: slides.length,
        navigation: {
          nextEl: this.querySelector('[data-aspadz-product-how-to-video-next]'),
          prevEl: this.querySelector('[data-aspadz-product-how-to-video-prev]'),
        },
        pagination: pagination
          ? {
              clickable: true,
              el: pagination,
              renderBullet(index, className) {
                return `
                  <button class="${className}" type="button" aria-label="Go to slide ${index + 1}">
                    <span class="aspadz-product-how-to-video__pagination-track">
                      <span class="aspadz-product-how-to-video__pagination-progress"></span>
                    </span>
                  </button>
                `;
              },
            }
          : undefined,
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 650,
        watchOverflow: true,
        on: {
          init: () => this.startAutoplayProgress(),
          slideChangeTransitionStart: this.handleSlideChangeTransitionStart,
        },
      });

      this.handleMotionPreferenceChange();
    }

    onClick(event) {
      const videoTrigger = event.target.closest('[data-aspadz-product-how-to-video-trigger]');
      if (videoTrigger && this.contains(videoTrigger)) {
        event.preventDefault();
        this.playInlineVideo(videoTrigger);
      }
    }

    handleSlideChangeTransitionStart() {
      this.stopInlineVideo({ resetProgress: true });
      this.isAutoplayLocked = false;
      this.startAutoplayProgress();
    }

    startAutoplayProgress() {
      this.stopAutoplayProgress();
      this.classList.remove('is-video-progressing');

      if (!this.swiper || this.motionQuery.matches || this.isAutoplayLocked || this.activeFrame) {
        if (this.motionQuery.matches) this.setProgressScale(1);
        return;
      }

      const duration = this.autoplayDelay || 5000;
      const startedAt = window.performance.now();
      this.setProgressScale(0);
      this.classList.add('is-progressing');

      const tick = (now) => {
        if (!this.swiper || this.motionQuery.matches || this.isAutoplayLocked || this.activeFrame) {
          this.stopAutoplayProgress();
          return;
        }

        const progress = Math.min((now - startedAt) / duration, 1);
        this.setProgressScale(progress);

        if (progress >= 1) {
          this.stopAutoplayProgress();
          this.setProgressScale(1);
          this.swiper.slideNext();
          return;
        }

        this.progressFrame = window.requestAnimationFrame(tick);
      };

      this.progressFrame = window.requestAnimationFrame(tick);
    }

    stopAutoplayProgress() {
      if (this.progressFrame) {
        window.cancelAnimationFrame(this.progressFrame);
        this.progressFrame = null;
      }

      this.classList.remove('is-progressing');
    }

    playInlineVideo(trigger) {
      const media = trigger.closest('[data-aspadz-product-how-to-video-media]');
      const videoUrl = trigger.dataset.videoUrl;
      if (!media || !videoUrl) return;

      this.stopInlineVideo({ resetProgress: true });
      this.stopAutoplayProgress();
      this.isAutoplayLocked = false;
      this.activeMedia = media;
      this.activeVideoTrigger = trigger;
      this.classList.add('is-video-progressing');
      this.setProgressScale(0);

      const source = this.getVideoSource(videoUrl);
      if (!source) {
        this.startAutoplayProgress();
        return;
      }

      const frame = document.createElement('div');
      frame.className = 'aspadz-product-how-to-video__inline-frame';
      frame.dataset.aspadzProductHowToVideoInlineFrame = '';
      media.classList.add('is-video-active');
      media.append(frame);
      this.activeFrame = frame;

      if (source.type === 'video') {
        this.renderNativeVideo(frame, source.src);
        return;
      }

      this.renderIframeVideo(frame, source.src, trigger.getAttribute('aria-label') || '');
    }

    renderNativeVideo(frame, src) {
      const video = document.createElement('video');
      video.src = src;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.addEventListener('play', this.handleVideoPlay);
      video.addEventListener('pause', this.handleVideoPause);
      video.addEventListener('ended', this.handleVideoEnded);
      video.addEventListener('timeupdate', this.handleVideoProgress);
      video.addEventListener('durationchange', this.handleVideoProgress);
      frame.append(video);
      this.activeVideo = video;
      const playPromise = video.play?.();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => this.markVideoPaused());
      }
    }

    renderIframeVideo(frame, src, title) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = title;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.append(iframe);
      this.activeIframe = iframe;
      this.isAutoplayLocked = true;
      this.classList.add('is-video-progressing');
      this.setProgressScale(0);
    }

    stopInlineVideo({ resetProgress = false } = {}) {
      this.isStoppingVideo = true;
      this.stopVideoProgress();

      if (this.activeVideo) {
        this.activeVideo.pause();
        this.activeVideo.removeEventListener('play', this.handleVideoPlay);
        this.activeVideo.removeEventListener('pause', this.handleVideoPause);
        this.activeVideo.removeEventListener('ended', this.handleVideoEnded);
        this.activeVideo.removeEventListener('timeupdate', this.handleVideoProgress);
        this.activeVideo.removeEventListener('durationchange', this.handleVideoProgress);
      }

      if (this.activeFrame) {
        this.activeFrame.remove();
      }

      if (this.activeMedia) {
        this.activeMedia.classList.remove('is-video-active');
      }

      this.activeMedia = null;
      this.activeFrame = null;
      this.activeVideo = null;
      this.activeIframe = null;
      this.activeVideoTrigger = null;
      this.isStoppingVideo = false;
      this.classList.remove('is-video-progressing');

      if (resetProgress) {
        this.setProgressScale(0);
      }
    }

    handleVideoPlay() {
      this.isAutoplayLocked = false;
      this.classList.add('is-video-progressing');
      this.syncVideoProgress();
      this.startVideoProgress();
    }

    handleVideoPause() {
      if (this.isStoppingVideo || !this.activeVideo || this.activeVideo.ended) return;
      this.markVideoPaused();
    }

    handleVideoEnded() {
      this.isAutoplayLocked = true;
      this.stopVideoProgress();
      this.setProgressScale(1);
      this.classList.add('is-video-progressing');
    }

    handleVideoProgress() {
      this.syncVideoProgress();
    }

    markVideoPaused() {
      this.isAutoplayLocked = true;
      this.stopVideoProgress();
      this.syncVideoProgress();
      this.classList.add('is-video-progressing');
    }

    startVideoProgress() {
      this.stopVideoProgress();

      const tick = () => {
        if (!this.activeVideo || this.activeVideo.paused || this.activeVideo.ended) return;
        this.syncVideoProgress();
        this.videoProgressFrame = window.requestAnimationFrame(tick);
      };

      this.videoProgressFrame = window.requestAnimationFrame(tick);
    }

    stopVideoProgress() {
      if (!this.videoProgressFrame) return;
      window.cancelAnimationFrame(this.videoProgressFrame);
      this.videoProgressFrame = null;
    }

    syncVideoProgress() {
      if (!this.activeVideo) return;

      const duration = this.activeVideo.duration;
      const currentTime = this.activeVideo.currentTime;

      if (!Number.isFinite(duration) || duration <= 0) {
        this.setProgressScale(0);
        return;
      }

      this.setProgressScale(currentTime / duration);
    }

    setProgressScale(value) {
      const normalizedValue = Math.max(0, Math.min(1, Number(value) || 0));
      this.progressScale = normalizedValue;
      this.style.setProperty('--aspadz-phtv-progress-scale', normalizedValue.toString());
    }

    handleMotionPreferenceChange() {
      const reduceMotion = this.motionQuery.matches;
      this.classList.toggle('aspadz-product-how-to-video--reduced-motion', reduceMotion);

      if (reduceMotion) {
        this.stopAutoplayProgress();
        this.stopVideoProgress();
        this.setProgressScale(1);
        return;
      }

      if (!this.activeFrame && !this.isAutoplayLocked) {
        this.startAutoplayProgress();
      }
    }

    getVideoSource(videoUrl) {
      let parsedUrl;

      try {
        parsedUrl = new URL(videoUrl, window.location.href);
      } catch (error) {
        return null;
      }

      const host = parsedUrl.hostname.replace(/^www\./, '').replace(/^m\./, '');
      const path = parsedUrl.pathname.replace(/^\/+/, '');
      const videoExtension = /\.(mp4|webm|ogg|ogv|mov)$/i;

      if (host === 'youtu.be') {
        const id = path.split('/')[0];
        return id ? { type: 'iframe', src: this.getYouTubeEmbedUrl(id, parsedUrl) } : null;
      }

      if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
        let id = parsedUrl.searchParams.get('v');

        if (!id && (path.startsWith('embed/') || path.startsWith('shorts/') || path.startsWith('live/'))) {
          id = path.split('/')[1];
        }

        return id ? { type: 'iframe', src: this.getYouTubeEmbedUrl(id, parsedUrl) } : null;
      }

      if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
        const id = path.split('/').find((part) => /^\d+$/.test(part));
        return id ? { type: 'iframe', src: `https://player.vimeo.com/video/${id}?autoplay=1` } : { type: 'iframe', src: parsedUrl.href };
      }

      if (videoExtension.test(parsedUrl.pathname)) {
        return { type: 'video', src: parsedUrl.href };
      }

      return { type: 'iframe', src: parsedUrl.href };
    }

    getYouTubeEmbedUrl(id, parsedUrl) {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
      });
      const start = this.getYouTubeStartTime(parsedUrl.searchParams.get('start') || parsedUrl.searchParams.get('t'));

      if (start) params.set('start', start);

      return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
    }

    getYouTubeStartTime(value) {
      if (!value) return '';
      if (/^\d+$/.test(value)) return value;

      const hours = value.match(/(\d+)h/);
      const minutes = value.match(/(\d+)m/);
      const seconds = value.match(/(\d+)s/);
      const total = (hours ? Number(hours[1]) * 3600 : 0) + (minutes ? Number(minutes[1]) * 60 : 0) + (seconds ? Number(seconds[1]) : 0);

      return total ? String(total) : '';
    }
  }

  if (!window.customElements.get('aspadz-product-how-to-video')) {
    window.customElements.define('aspadz-product-how-to-video', AspadzProductHowToVideo);
  }
})();
